import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subcontractAssignmentApi } from '../services/subcontractAssignmentApi';
import { useInvalidateWorkOrder } from './useWorkOrders';
import { subcontractorKeys } from './useSubcontractors';
import type {
  CreateSubcontractAssignmentRequest,
  RateSubcontractAssignmentRequest,
  SubcontractAssignment,
  SubcontractResolution,
  UpdateSubcontractAssignmentRequest,
} from '../types/service.types';

/**
 * D18-01 — Sublet assignments are read from their own endpoint, never off the
 * WorkOrder resource (SUB-10). The key nests under `['work-orders', id]` so it
 * follows the work order's lifecycle in the cache.
 *
 * Dispatch / complete move the job asynchronously (a queued listener, D18-08):
 * the section arms a bounded work-order poll after those succeed (18-20), which
 * is why every mutation here also invalidates the work-order detail.
 */
export const subcontractKeys = {
  list: (workOrderId: string) => ['work-orders', workOrderId, 'subcontract-assignments'] as const,
};

export function useSubcontractAssignments(workOrderId: string) {
  return useQuery({
    queryKey: subcontractKeys.list(workOrderId),
    queryFn: () => subcontractAssignmentApi.list(workOrderId),
    enabled: !!workOrderId,
  });
}

/** Every mutation refreshes the sublet list AND the owning work order. */
function useAssignmentMutation<TVars>(
  workOrderId: string,
  fn: (vars: TVars) => Promise<SubcontractAssignment>,
  options: { invalidateCatalogue?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const invalidateWorkOrder = useInvalidateWorkOrder();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subcontractKeys.list(workOrderId) });
      invalidateWorkOrder(workOrderId);
      if (options.invalidateCatalogue) {
        queryClient.invalidateQueries({ queryKey: subcontractorKeys.lists() });
      }
    },
  });
}

export function useRequestSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(workOrderId, (data: CreateSubcontractAssignmentRequest) =>
    subcontractAssignmentApi.request(workOrderId, data),
  );
}

export function useUpdateSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, data }: { id: string; data: UpdateSubcontractAssignmentRequest }) =>
      subcontractAssignmentApi.update(id, data),
  );
}

export function useQuoteSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({
      id,
      quotedCents,
      markupBps,
      document,
    }: {
      id: string;
      quotedCents: number;
      markupBps?: number;
      document?: File;
    }) => {
      const formData = new FormData();
      formData.append('quoted_cents', String(quotedCents));
      if (markupBps !== undefined) formData.append('markup_bps', String(markupBps));
      if (document) formData.append('document', document);
      return subcontractAssignmentApi.quote(id, formData);
    },
  );
}

export function useApproveSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, customerSignatureId }: { id: string; customerSignatureId: string }) =>
      subcontractAssignmentApi.approve(id, customerSignatureId),
  );
}

export function useDispatchSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, promisedReturnDate }: { id: string; promisedReturnDate: string }) =>
      subcontractAssignmentApi.dispatch(id, promisedReturnDate),
  );
}

export function useStartSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(workOrderId, ({ id }: { id: string }) =>
    subcontractAssignmentApi.start(id),
  );
}

export function useCompleteSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(workOrderId, ({ id }: { id: string }) =>
    subcontractAssignmentApi.complete(id),
  );
}

export function useInvoiceSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({
      id,
      billedToDealerCents,
      subInvoiceNumber,
      document,
    }: {
      id: string;
      billedToDealerCents: number;
      subInvoiceNumber: string;
      document?: File;
    }) => {
      const formData = new FormData();
      formData.append('billed_to_dealer_cents', String(billedToDealerCents));
      formData.append('sub_invoice_number', subInvoiceNumber);
      if (document) formData.append('document', document);
      return subcontractAssignmentApi.invoice(id, formData);
    },
  );
}

export function useSettleSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(workOrderId, ({ id }: { id: string }) =>
    subcontractAssignmentApi.settle(id),
  );
}

export function useDisputeSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, reason }: { id: string; reason: string }) => subcontractAssignmentApi.dispute(id, reason),
  );
}

/**
 * D19-20 / D19-28 — rate a settled sublet. Also invalidates the catalogue
 * lists: the subcontractor's average is recomputed by a queued job (RATE-02),
 * so the refetch may return the old figure once (eventually consistent).
 */
export function useRateSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, body }: { id: string; body: RateSubcontractAssignmentRequest }) =>
      subcontractAssignmentApi.rate(id, body),
    { invalidateCatalogue: true },
  );
}

export function useResolveSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({
      id,
      resolution,
      reason,
    }: {
      id: string;
      resolution: SubcontractResolution;
      reason?: string;
    }) => subcontractAssignmentApi.resolve(id, resolution, reason),
  );
}

export function useCancelSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, reason }: { id: string; reason: string }) => subcontractAssignmentApi.cancel(id, reason),
  );
}

export function useDeclineSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, reason }: { id: string; reason: string }) => subcontractAssignmentApi.decline(id, reason),
  );
}

export function useVoidSubcontractAssignment(workOrderId: string) {
  return useAssignmentMutation(
    workOrderId,
    ({ id, reason }: { id: string; reason: string }) =>
      subcontractAssignmentApi.voidAssignment(id, reason),
  );
}
